import * as React from "react";
import {
  arc as d3arc,
  symbolCircle,
  symbolStar,
  symbol as d3symbol,
} from "d3-shape";
import FormatDecimal from "../FormatDecimal";
import { useSpring, animated, config } from "@react-spring/web";
import { useGroopitApiContext, VizDTO, IVizDTO } from "groopit-js";
import { useEffect, useState } from "react";

const width = 160;
const height = 150;
const radius = 60;

const progress_width = 10;

const arcEnd = (endAngle: number) => {
  return (
    d3arc().context(null)({
      innerRadius: radius - progress_width / 2,
      outerRadius: radius + progress_width / 2,
      startAngle: Math.PI,
      endAngle,
    }) ?? undefined
  );
};

const circle = d3symbol().type(symbolCircle).size(350);
const star = d3symbol().type(symbolStar).size(100);

const GoalCircle = (props: { percent: number }) => {
  const { percent } = props;
  return (
    <g transform={`translate(${width / 2},${height / 2})`}>
      <path d={arcEnd(2.5 * Math.PI)} fill="lightgray"></path>
      <path d={arcEnd(percent * 1.5 * Math.PI + Math.PI)} fill="#D46830"></path>
      <path
        d={circle(undefined) ?? undefined}
        fill="lightgray"
        transform={`translate(${radius},5)`}
      ></path>
      <path
        d={star(undefined) ?? undefined}
        fill="white"
        transform={`translate(${radius},5)`}
      ></path>
    </g>
  );
};

const AnimatedGoalCircle = animated(GoalCircle);

const ProgressCircle = (props: { max: number }) => {
  const { max } = { ...props };
  const { percent } = useSpring({
    from: { percent: 0 },
    to: { percent: max },
    config: config.wobbly,
  });
  return <AnimatedGoalCircle percent={percent}></AnimatedGoalCircle>;
};

export interface GoalVizProps {
  current: number;
  goal: number;
  weekly_delta: number;
  label?: string;
  format: string;
}

export const GoalViz = (props: GoalVizProps): JSX.Element => {
  const { current, goal, weekly_delta, label, format } = { ...props };
  //
  // If the format is a percentage, then divice the goal by 100
  // to account for the fact that the goal is an integer rather than a percent.
  //
  const g = format[0] === "P" ? goal / 100 : goal;
  return (
    <div className="flex items-center divide-x">
      <div className="flex-grow">
        <svg className="m-auto" width={width} height={height}>
          <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle">
            <tspan x="50%" className="counter-number">
              {Math.round((current * 100) / g)}%
            </tspan>
          </text>
          <ProgressCircle max={current / g} />
        </svg>
      </div>
      <div className="flex-grow" style={{ padding: "0px 8px" }}>
        <div className="text-center">
          GOAL: {FormatDecimal(g, format)}
          <div className="text-center">{label}</div>
        </div>
        <div className="text-center">
          ACTUAL: {FormatDecimal(current, format)}
        </div>
        {weekly_delta > 0 && (
          <div className="text-uppercase text-center text-color-primary">
            Up {FormatDecimal(weekly_delta, format)} this week
          </div>
        )}
      </div>
    </div>
  );
};

//
// Note this React implementation is used only for live preview
// mode while creating or editing. The display version is
// rendered serverside by GoalVizViewModel.cshtml and they need
// to be kept in sync manually.
//
// We could revisit this in the future if the perf overhead
// of the client-side React implementation is not an issue.
//
interface State {
  GoalCount: number;
  Counter: number;
  WeeklyDelta: number;
  Format: string;
}

type GoalWrapperProps = Omit<IVizDTO, "title" | "descripton">;

export const GoalVizPreview = (props: GoalWrapperProps): JSX.Element => {
  const { viz } = useGroopitApiContext();
  const [state, setState] = useState<State>();
  useEffect(() => {
    const getData = async () => {
      const json = await viz.getDataPOST(new VizDTO({ title: "", ...props }));
      setState(json);
    };

    getData();
  }, [props]);

  if (!state) {
    return <div>Loading...</div>;
  } else {
    const goal_props: GoalVizProps = {
      current: state.Counter,
      goal: state.GoalCount,
      weekly_delta: state.WeeklyDelta,
      label: props.label,
      format: state.Format,
    };
    return <GoalViz {...goal_props} />;
  }
};
